import { format } from "date-fns";
import { UseFormReturn } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { useUpdateTeamLeaderMutation } from "../../../../features/projectApi";
import { useFindOneQuery } from "../../../../features/usersApi";
import { addBannerWithTimeout } from "../../../lib/centralizedBanner/slice";
import { keycloak } from "../../../lib/keycloak";
import { useAppDispatch, useAppSelector } from "../../../lib/redux/hooks";
import { groupBy } from "../../../lib/utils";
import Input from "../../../ui/molecules/input";
import { SelectCustom } from "../../../ui/molecules/select";
import { Spinner } from "../../../ui/molecules/spinner";
import TextArea from "../../../ui/molecules/textArea";
import { UserRole } from "../../users/dto";
import { SelectUser } from "../../users/select/userSelect";
import { ProjectDTO, ProjectStateEnum } from "../dto";
import { BindCustomerFormData, ProjectEditFormData, } from "./validation";

interface Props {
    form: UseFormReturn<ProjectEditFormData>;
    bindCustomer: UseFormReturn<BindCustomerFormData>;
    project: ProjectDTO
}
export function Form({ form, project, bindCustomer }: Props) {
    const atecosState = useAppSelector(state => state.atecos)
    const usersState = useAppSelector(state => state.users)

    const [updateTeamLeader, { isLoading: isUpdatingTeamLeader }] = useUpdateTeamLeaderMutation()
    const handleUpdateTeamLeader = async (teamLeaderId: string | null) => {
        try {
            await updateTeamLeader({ id: project.id!, data: { teamLeaderId } }).unwrap()
            dispatch(addBannerWithTimeout({
                variant: "success",
                message: "Team leader aggiornato con successo"
            }))
        } catch (error) {
            dispatch(addBannerWithTimeout({
                variant: "error",
                message: "Errore durante l'aggiornamento del team leader"
            }))
        }
    }

    const { data: customer } = useFindOneQuery(
        bindCustomer.watch('customerId')!,
        {
            skip: !bindCustomer.watch('customerId')
        }
    )
    const dispatch = useAppDispatch()

    return (
        <div className="gap-[24px] h-full max-h-full flex flex-col w-[100%] p-[16px]">
            <div className="p-[24px] h-full overflow-auto gap-[24px] flex flex-col bg-white shadow-sm rounded-[12px]">
                <div className="flex gap-6 items-flex-start">
                    <Input
                        label="Nome del progetto"
                        disabled
                        value={project.name}
                    />
                    {
                        keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator') ?
                            <Input
                                key={'commercials'}
                                label="Commerciali"
                                disabled
                                value={project.commercials?.map(commercial => (commercial.name + ' ' + commercial.surname)).join(',')}
                                placeholder={"Commerciali"}
                            /> :
                            <SelectUser
                                isMulti={true}
                                {...form.register("commercialIds")}
                                id="select-commercials-edit-project"
                                key="select-commercials-edit-project"
                                role={UserRole.commercial}
                                value={
                                    (
                                        JSON.stringify(form.watch("commercialIds")) === JSON.stringify(project?.commercials?.map(comm => comm.id))) ?
                                        (project?.commercials?.map(commercial => ({ label: commercial.name + ' ' + commercial.surname, value: commercial.id })) ?? []) :
                                        (form.watch("commercialIds") ?? [])
                                }
                                onChange={(v) => form.setValue("commercialIds", v ?? [])}
                                label="Commerciali"
                                placeholder={"Seleziona i commerciali"}
                                disabled={!usersState.findMeResponse?.isAutonomous && keycloak.hasRealmRole("commercial")}
                                isClearable
                            />
                    }
                    {
                        keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('commercial') || keycloak.hasRealmRole('collaborator') ?
                            <Input
                                key={'teamleaders'}
                                label="Team Leader"
                                disabled
                                value={project?.teamLeader?.name + ' ' + project?.teamLeader?.surname}
                                placeholder={"Team Leader"}
                            /> :
                            (
                                isUpdatingTeamLeader ?
                                    <div className="flex w-full justify-center items-end">
                                        <Spinner size={32} />
                                    </div> :
                                    <SelectUser
                                        isMulti={false}
                                        isClearable={false}
                                        key={project?.teamLeader ? project?.teamLeader.id : 'no-team-leader'}
                                        id={project?.teamLeader ? project?.teamLeader.id : 'no-team-leader'}
                                        role={UserRole.team_leader}
                                        value={
                                            project?.teamLeader ? { label: project?.teamLeader.name + ' ' + project?.teamLeader.surname, value: project?.teamLeader.id } : null
                                        }
                                        onChange={e => {
                                            handleUpdateTeamLeader(typeof e === 'string' ? e : null)
                                        }}
                                        label="Team Leader"
                                        placeholder={"Seleziona il team leader"}
                                    />
                            )
                    }
                </div>
                <div className="h-[1px] w-full bg-neutral-200" />
                <div className="flex gap-6 items-start">
                    {
                        keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator') ?
                            <Input
                                key={'teamleaders'}
                                label="Ragione Sociale"
                                disabled
                                value={project?.customer?.companyName}
                                placeholder={"Cliente"}
                            /> :
                            <SelectUser
                                disabled={project?.customer !== null}
                                isMulti={false}
                                key={project?.customer ? project?.customer.id : 'no-customer'}
                                id={project?.customer ? project?.customer.id : 'no-customer'}
                                role={UserRole.customer}
                                value={
                                    project?.customer === null ? '' :
                                        project?.customer ?
                                            { label: project?.customer?.companyName!, value: project?.customer?.id } :
                                            { label: customer?.user?.companyName ?? '', value: customer?.user?.id ?? '' }
                                }
                                onChange={(v) => bindCustomer.setValue('customerId', v ? v : '')}
                                label="Ragione Sociale"
                                placeholder={"Seleziona il cliente"}
                            />
                    }
                    <Input
                        label="P. IVA"
                        disabled
                        value={project?.customer === null && (!customer || !customer?.user || !customer?.user?.companyVat) ? '' : (customer?.user?.companyVat ?? project?.customer?.companyVat)}
                        placeholder="P. IVA"
                    />
                    <Input
                        label="Referente"
                        placeholder="Referente"
                        disabled
                        value={project?.customer !== null && project ? (project.customer?.name + ' ' + project.customer?.surname) : customer ? (customer?.user?.name + ' ' + customer?.user?.surname) : ''}
                    />
                </div>
                <div className="flex gap-6 items-start">
                    <SelectCustom
                        key={project?.customer ? project?.customer.companyAtecoCode : 'no-customer-ateco'}
                        placeholder={"Cod. Ateco"}
                        disabled
                        defaultValue={project?.customer === null && (!customer || !customer?.user || !customer?.user?.companyAtecoCode) ? '' : (customer?.user?.companyAtecoCode ?? project?.customer?.companyAtecoCode)}
                        options={atecosState.findAllAtecosResponse.map(ateco => ({ value: ateco.atecoCode, label: ateco.atecoCode + ' - ' + ateco.atecoDescription }))}
                        label="Cod. Ateco"
                    />
                    <SelectCustom
                        key={project?.customer ? project?.customer.companySector : 'no-customer-sector'}
                        placeholder={"Settore"}
                        disabled
                        defaultValue={project?.customer === null && (!customer || !customer?.user || !customer?.user?.companySector) ? '' : (customer?.user?.companySector ?? project?.customer?.companySector)}
                        options={groupBy(atecosState.findAllAtecosResponse, 'sector').map(sector => ({ label: sector.key, value: sector.key }))}
                        label="Settore"
                    />
                    <NumericFormat
                        prefix="€ "
                        value={project?.customer === null && (!customer || !customer?.user || !customer?.user?.companyRevenue) ? '' : (customer?.user?.companyRevenue ?? project?.customer?.companyRevenue)}
                        thousandSeparator="."
                        decimalSeparator=","
                        decimalScale={2}
                        fixedDecimalScale
                        customInput={Input}
                        id={"new-user-request-company-revenue"}
                        key={"new-user-request-company-revenue"}
                        placeholder={"Fatturato"}
                        label="Fatturato"
                        disabled
                    />
                </div>
                <div className="flex gap-6 items-start">
                    <SelectCustom placeholder={""} defaultValue={ProjectStateEnum.TOSTART} options={[{ label: "Da iniziare", value: ProjectStateEnum.TOSTART }]} disabled label="Stato" />
                    <Input disabled label="Data inizio" value={project?.startDate ? format(new Date(project?.startDate), 'dd/MM/yyyy') : ''} />
                    <Input disabled label="Data fine" placeholder="Data fine" value={project?.endDate ? format(new Date(project?.endDate), 'dd/MM/yyyy') : ''} />
                </div>
                <TextArea
                    label="Descrizione"
                    defaultValue={form.watch("description") ?? project?.description}
                    onChangeText={e => form.setValue("description", e)}
                    disabled={!usersState.findMeResponse?.isAutonomous && keycloak.hasRealmRole("commercial")}
                />
            </div>
        </div>
    )
}